import React, { JSX, useContext, useEffect, useState } from "react";
import cx from "classnames";
import { concat, max, startCase } from "lodash";
import { BaseTickContentProps, TooltipContentProps } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingLineOfBusinessEnum, CompetitiveInsightsTabTypeEnum, CompetitiveInsightsViewTypeEnum, ContractDisplayInformation } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { Affiliations, EffectivenessAndEfficiency, HospitalAndPcpCount, MarketPenetration, NetworkComposition, ProviderCount, ProviderOverlap, ProvidersByNetwork } from "@app/overviews/network-benchmarking-dashboard/tiles";
import { ProviderCountTypeEnum, ProvidersByNetworkData, TotalAndMarketPenetrationTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { printNumberAsString } from "@app/shared/waffles";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import CompetitiveInsightsManage from "@app/overviews/network-benchmarking-dashboard/manage/CompetitiveInsightsManage";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import NetworkComparisonFilters from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/NetworkComparisonFilters";
import QHPFilters from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFilters";
import QHPNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPNetworkFiltersTable";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import MedicaidFilters from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFilters";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import MedicaidNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidNetworkFiltersTable";
import { MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import MedicareFilters from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFilters";
import MedicareNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareNetworkFiltersTable";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import PageHeader from "@app/shared/PageHeader";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { ToggleButton } from "@app/shared/toggle-button/ToggleButton";

const CompetitiveInsightsDefaultView = (): JSX.Element => {
  const [selectedView, setSelectedView] = useState<CompetitiveInsightsViewTypeEnum>(CompetitiveInsightsViewTypeEnum.MapView);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const userContext = useContext(UserContext);

  useEffect(() => {
    if (!benchmarkingFiltersContext.displayedPrimaryFilters && selectedView !== CompetitiveInsightsViewTypeEnum.MapView) {
      setSelectedView(CompetitiveInsightsViewTypeEnum.MapView);
    }
  }, [benchmarkingFiltersContext.displayedPrimaryFilters]);

  useEffect(() => {
    if (benchmarkingContext.hospitalAndPcpCountDataLoading && benchmarkingFiltersContext.selectedTab !== CompetitiveInsightsTabTypeEnum.NetworkComparison) {
      benchmarkingFiltersContext.setSelectedTab(CompetitiveInsightsTabTypeEnum.NetworkComparison);
      window.scrollTo(0, 0);
    }
  }, [benchmarkingContext.hospitalAndPcpCountDataLoading]);

  const customTooltip = (
    props: TooltipContentProps<ValueType, string | number>,
    titleKey: string,
    round?: number,
  ): JSX.Element => {
    // this accounts for the chart tooltip for all charts except for the ghost tile, which is built in the ghost component itself
    if (props.payload?.length) {
      const order: number[] = Array.from(Array(props.payload.length)).map((_, index) => index).reverse();

      const tooltipItems: JSX.Element[] = order.map((index) => {
        const payloadItem = props.payload[index];

        const payloadValue = payloadItem.value as number;
        const displayValue = !!payloadValue ? (
          !!round ? payloadValue.toFixed(round) : printNumberAsString(payloadValue, "0")
        ) : 0;

        return (
          <p className="mb-0" key={index}>
            <span>{startCase(payloadItem.name as string)}:</span> {displayValue}
          </p>
        );
      });

      const contractDisplayInformation: ContractDisplayInformation = benchmarkingFiltersContext.getContractDisplayInformation(props.payload[0].payload[titleKey])

      if (!!contractDisplayInformation) {
        return (
          <div className="qes-dashboard__custom-tooltip">
            {!!contractDisplayInformation.parentOrganizationName && (
              <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.parentOrganizationName}</p>
            )}
            <p className="qes-dashboard__tooltip-title mb-0">{contractDisplayInformation.networkName}</p>
            {tooltipItems}
          </div>
        );
      }
    }
  }

  const formatTabId = (tab: string): string => {
    return tab.toLowerCase().replace(/\ /g, "-");
  }

  const canViewManageTab = (): boolean => {
    return featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_CI_PROJECT_CONFIGURATION_ADMIN) &&
      (userContext.roles.includes(Roles.QUEST_ADMIN) || userContext.roles.includes(Roles.CLIENT_ADMIN) || userContext.roles.includes(Roles.NETWORK_PERFORMANCE_ADMIN));
  }

  const getVisibleTabs = (): CompetitiveInsightsTabTypeEnum[] => {
    return Object.values(CompetitiveInsightsTabTypeEnum).filter((tab) => (
      tab !== CompetitiveInsightsTabTypeEnum.Manage || canViewManageTab()
    ));
  }

  const getNetworkSelection = (): JSX.Element => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <MedicareFilters
            isRedesign={true}
            selectedView={selectedView}
          />
        );
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (
          <QHPFilters
            isRedesign={true}
            selectedView={selectedView}
          />
        );
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        if (featureFlagContext.enabledFeatureFlags.has(Features.COMPETITIVE_INSIGHTS_MEDICAID)) {
          return (
            <MedicaidFilters
              selectedView={selectedView}
            />
          );
        }
      default:
        return (
          <MedicareFilters
            isRedesign={true}
            selectedView={selectedView}
          />
        );
    }
  }

  const getTotalProviderCount = (contractId: string): number => {
    const totalProviderCount: ProvidersByNetworkData = benchmarkingContext.totalProviderCount.find(x => x.contractId === contractId);

    return totalProviderCount?.providerCount;
  }

  const getCustomXAxis = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const CustomXAxisTick = ({ x, y, payload }: BaseTickContentProps): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              const splitContract: string[] = contract.contractId.split("_");

              return (
                <g transform={`translate(${Number(x) - 5},${Number(y) - 10}) rotate(-45)`} textAnchor="end">
                  <text x={0} y={0} dy={16} style={{ fontSize: "12px" }}>
                    <tspan className="text-muted" textAnchor="end" x="0">
                      {splitContract[0]}
                    </tspan>
                    <tspan textAnchor="end" x="0" dy="15">
                      {splitContract[1]}
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomXAxisTick;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        const CustomXAxisTickSingle = ({ x, y, payload }: BaseTickContentProps): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              return (
                <g transform={`translate(${Number(x) - 5},${y}) rotate(-45)`} textAnchor="end">
                  <text x={0} y={0} dy={16} style={{ fontSize: "14px" }}>
                    <tspan textAnchor="end" x="0">
                      {contract.contractId}
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomXAxisTickSingle;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        const CustomXAxisTickMedicaid = ({ x, y, payload }: BaseTickContentProps): JSX.Element => {
          if (!!payload?.value) {
            const contract: MedicaidNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as MedicaidNetwork;

            if (!!contract) {
              return (
                <g transform={`translate(${Number(x) - 5},${y}) rotate(-45)`}>
                  <foreignObject x={-50} y={0} width={60} height={20}>
                    <EllipsisTooltip text={contract.parentOrganization} maxLength={6} />
                  </foreignObject>
                </g>
              );
            }
          }
        }

        return CustomXAxisTickMedicaid;
      default:
        return undefined;
    }
  }

  const getCustomYAxis = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const CustomYAxisTick = ({ x, y, payload }: BaseTickContentProps): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              const splitContract: string[] = contract.contractId.split("_");

              return (
                <g>
                  <text x={x} y={Number(y) - 3} textAnchor="end" style={{ fontSize: "12px" }}>
                    <tspan className="text-muted" textAnchor="start" x="0">
                      {splitContract[0]}
                    </tspan>
                    <tspan textAnchor="start" x="0" dy="15">
                      {splitContract[1]}
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomYAxisTick;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        const CustomYAxisTickMedicaid = ({ y, payload }: BaseTickContentProps): JSX.Element => {
          if (!!payload?.value) {
            const contract: MedicaidNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as MedicaidNetwork;

            if (!!contract) {
              return (
                <foreignObject x={0} y={Number(y) - 12} width={60} height={20}>
                  <EllipsisTooltip text={contract.parentOrganization} maxLength={6} />
                </foreignObject>
              );
            }
          }
        }

        return CustomYAxisTickMedicaid;
      default:
        return undefined;
    }
  }

  const getDefaultCompetitiveInsightsView = (): JSX.Element => {
    const getNetworkSummaryByLineOfBusiness = (): JSX.Element => {
      switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          return (
            <QHPNetworkFiltersTable
              colorMap={benchmarkingContext.hContractColorMap}
              data={concat(networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork, networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks) as QHPNetwork[]}
              isNetworkSummary={true}
              parentOrganizationName="network-summary"
              selectedContractIds={[]}
            />
          );
        case BenchmarkingLineOfBusinessEnum.Medicare:
          return (
            <MedicareNetworkFiltersTable
              colorMap={benchmarkingContext.hContractColorMap}
              data={concat(networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork, networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks) as MedicareNetwork[]}
              isNetworkSummary={true}
              parentOrganizationName="network-summary"
              selectedContractIds={[]}
            />
          );
        case BenchmarkingLineOfBusinessEnum.Medicaid:
          return (
            <MedicaidNetworkFiltersTable
              colorMap={benchmarkingContext.hContractColorMap}
              data={concat(networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork, networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks) as MedicaidNetwork[]}
              isNetworkSummary={true}
              parentOrganizationName="network-summary"
              selectedContractIds={[]}
            />
          );
        default:
          return;
      }
    }

    return (
      <React.Fragment>
        <NetworkComparisonFilters isRedesign={true} />

        {!!benchmarkingFiltersContext.displayedNetworkFilters.length && (
          <React.Fragment>
            <div className="row">
              <DashboardTile
                title="Network Summary"
                widthClass="col-12"
              >
                <div className="row">
                  <div className="col-md-6 px-5">
                    {getNetworkSummaryByLineOfBusiness()}
                  </div>

                  <div className="col-md-6 px-5">
                    {featureFlagContext.enabledFeatureFlags.has(Features.USE_AHD_IN_NETWORK_COMPARISON_OF_HOSPITALS) ? (
                      <>
                        <h2 className="qes-dashboard-tile__title">Hospital and PCP Count</h2>
                        <label className="copy-text-default d-block mb-5 text-muted">Displays the total number of hospitals and PCPs for each of the chosen comparison networks.
                          Hospitals are counted using unique CCNs. All other specialties are counted using unique NPIs.
                        </label>
                      </>
                    ) : (
                      <>
                        <h2 className="qes-dashboard-tile__title">Hospital and PCP Count</h2>
                        <label className="copy-text-default d-block mb-5 text-muted">Displays the total number of hospitals and PCPs for each of the chosen comparison networks.
                          Both numbers are counted using unique NPIs.
                        </label>
                      </>
                    )}
                    {benchmarkingContext.hospitalAndPcpCountDataLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <HospitalAndPcpCount
                        canDrillIn={true}
                        colorMap={benchmarkingContext.hContractColorMap}
                        customTooltip={customTooltip}
                        data={benchmarkingContext.hospitalAndPcpCountData}
                        onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                      />
                    )}
                  </div>
                </div>
              </DashboardTile>
            </div>

            <div className="row">
              {benchmarkingContext.providerOverlapDataLoading ? (
                <div className="col-12">
                  <div className="text-center">
                    <LoadingSpinner />
                  </div>
                </div>
              ) : (
                <React.Fragment>
                  <div className="col-md-6">
                    <div className="row">
                      <div className="col-md-12">
                        <label className="label-m mt-3 mb-0 d-block">
                          Provider Overlap
                        </label>

                        <label className="copy-text-default d-block mb-5 text-muted">
                          This is the comparison of your network to your competitor&#39;s network
                        </label>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mt-5">
                        <ProviderOverlap
                          data={benchmarkingContext.providerOverlapData}
                          onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                          providerOverlapHeight={400}
                        />
                      </div>
                    </div>
                  </div>

                  {benchmarkingFiltersContext.selectedNetworks.length > 2 && (
                    <div className="col-md-6">
                      <ProviderCount
                        customTooltip={customTooltip}
                        data={benchmarkingContext.providerOverlapData?.inNetworkCounts}
                        onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                        providerCountType={ProviderCountTypeEnum.InNetwork}
                      />

                      <ProviderCount
                        customTooltip={customTooltip}
                        data={benchmarkingContext.providerOverlapData?.outNetworkCounts}
                        onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                        providerCountType={ProviderCountTypeEnum.OutOfNetwork}
                      />
                    </div>
                  )}
                </React.Fragment>
              )}
            </div>

            <div className="row">
              <div className="col-12">
                <hr className="mt-4 mb-4" />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="row" id="benchmarking-dashboard__providers-by-network--container">
                  <DashboardTile
                    loading={benchmarkingContext.totalAndMarketPenetrationTab === TotalAndMarketPenetrationTabTypeEnum.ProviderCount ? benchmarkingContext.providersByNetworkDataLoading : benchmarkingContext.fullMarketPenetrationDataLoading}
                    title={benchmarkingContext.totalAndMarketPenetrationTab === TotalAndMarketPenetrationTabTypeEnum.ProviderCount ? "Providers by Network" : "Network Breadth (Percent of Market)"}
                    subtitle={(
                      benchmarkingContext.totalAndMarketPenetrationTab === TotalAndMarketPenetrationTabTypeEnum.ProviderCount ?
                        "This displays a unique count of NPIs within each network." :
                        "Shows the breadth of each network as it relates to the QES market data. Percentages are based on the unique count of NPIs for each network."
                    ) +
                      " Each element below can be clicked to reveal additional details."
                    }
                    widthClass="col-md-12"
                  >
                    {benchmarkingContext.totalAndMarketPenetrationTab === TotalAndMarketPenetrationTabTypeEnum.ProviderCount ? (
                      <ProvidersByNetwork
                        customTooltip={customTooltip}
                        customYAxisTick={getCustomYAxis()}
                        data={benchmarkingContext.providersByNetworkData}
                        onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                        selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                      />
                    ) : (
                      <MarketPenetration
                        customYAxisTick={getCustomYAxis()}
                        data={benchmarkingContext.fullMarketPenetrationData}
                        getContractDisplayInformation={benchmarkingFiltersContext.getContractDisplayInformation}
                        largetInNetworkOnlyValue={max(benchmarkingContext.fullMarketPenetrationData.map(x => x.inNetworkOnlyDisplayPercentage))}
                        onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                        selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                      />
                    )}
                    <div className="row">
                      <div className="offset-lg-3 col-lg-6">
                        <ToggleButton
                          onClick={(key: string) => benchmarkingContext.setTotalAndMarketPenetrationTab(key as TotalAndMarketPenetrationTabTypeEnum)}
                          selectedButton={benchmarkingContext.totalAndMarketPenetrationTab}
                          buttons={{ [TotalAndMarketPenetrationTabTypeEnum.NetworkBreadth]: TotalAndMarketPenetrationTabTypeEnum.NetworkBreadth, [TotalAndMarketPenetrationTabTypeEnum.ProviderCount]: TotalAndMarketPenetrationTabTypeEnum.ProviderCount }}
                          testId="benchmarking-dashboard-total-and-market-penetration"
                          groupLabel="Displayed Data Type"
                        />
                      </div>
                    </div>
                  </DashboardTile>
                </div>
                <div className="row">
                  <Affiliations
                    affiliationsVersion={benchmarkingContext.affiliationsVersion}
                    affiliationsVersionLoading={benchmarkingContext.affiliationsVersionLoading}
                    data={benchmarkingContext.affiliationsData}
                    getContractDisplayInformation={benchmarkingFiltersContext.getContractDisplayInformation}
                    loading={benchmarkingContext.affiliationsDataLoading}
                    onContractAffiliationsClick={benchmarkingContext.onContractAffiliationsClick}
                    onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                    selectedAffiliationsNetwork={benchmarkingContext.selectedAffiliationsNetwork}
                    selectedLineOfBusiness={benchmarkingFiltersContext.selectedLineOfBusiness}
                    selectedNetworks={concat(networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork, networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks)}
                    widthClass="col-12"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="row">
                  <NetworkComposition
                    customYAxisTick={getCustomYAxis()}
                    data={benchmarkingContext.networkCompositionData}
                    getContractDisplayInformation={benchmarkingFiltersContext.getContractDisplayInformation}
                    loading={benchmarkingContext.networkCompositionDataLoading}
                    onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                    selectedVolumeIndicators={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.volumeIndicators}
                  />
                </div>

                <div className="row">
                  <EffectivenessAndEfficiency
                    chartType={"effectiveness"}
                    customTooltip={customTooltip}
                    customXAxisTick={getCustomXAxis()}
                    data={benchmarkingContext.effectivenessAndEfficiencyData}
                    getTotalProviderCount={getTotalProviderCount}
                    loading={benchmarkingContext.effectivenessAndEfficiencyDataLoading}
                    onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                    selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                  />
                  <EffectivenessAndEfficiency
                    chartType={"efficiency"}
                    customTooltip={customTooltip}
                    customXAxisTick={getCustomXAxis()}
                    data={benchmarkingContext.effectivenessAndEfficiencyData}
                    getTotalProviderCount={getTotalProviderCount}
                    loading={benchmarkingContext.effectivenessAndEfficiencyDataLoading}
                    onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                    selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  const getTabView = (tab: CompetitiveInsightsTabTypeEnum): JSX.Element => {
    switch (tab) {
      case CompetitiveInsightsTabTypeEnum.NetworkSelection:
        return getNetworkSelection();
      case CompetitiveInsightsTabTypeEnum.NetworkComparison:
        return getDefaultCompetitiveInsightsView();
      case CompetitiveInsightsTabTypeEnum.Manage:
        return <CompetitiveInsightsManage />;
      default:
        break;
    }
  }

  const getViewIcon = (viewType: CompetitiveInsightsViewTypeEnum): QesIcons => {
    switch (viewType) {
      case CompetitiveInsightsViewTypeEnum.NetworkView:
        return QesIcons.List;
      case CompetitiveInsightsViewTypeEnum.MapView:
        return QesIcons.MapV1;
      default:
        return;
    }
  }

  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Competitive Insights Dashboard" />

      <div className="row">
        <div className="col-md-12">
          <div className="tabs-style-2 ci-sticky-tabs">
            <div className="d-flex justify-content-between align-items-center">
              <ul id="competitive-insights__dashboard--tabs" className="nav nav-tabs" role="menu">
                {getVisibleTabs().map((tab, index) => (
                  (
                    <li key={index} className={`nav-item${(tab === CompetitiveInsightsTabTypeEnum.NetworkComparison && !benchmarkingFiltersContext.displayedNetworkFilters?.length) ? " disabled" : ""}`}>
                      <a
                        className={`nav-link${tab === benchmarkingFiltersContext.selectedTab ? " active" : ""}${(tab === CompetitiveInsightsTabTypeEnum.NetworkComparison && !benchmarkingFiltersContext.displayedNetworkFilters?.length) ? " disabled" : ""}`}
                        data-bs-toggle="tab"
                        href={`#${formatTabId(tab)}-tab`}
                        onClick={() => benchmarkingFiltersContext.setSelectedTab(tab)}
                      >
                        {tab}
                      </a>
                    </li>
                  )
                ))}
              </ul>
              {benchmarkingFiltersContext.selectedTab === CompetitiveInsightsTabTypeEnum.NetworkSelection && (
                <div className="ci-icon-toggle">
                  {Object.values(CompetitiveInsightsViewTypeEnum).map((viewType, index) => (
                    (
                      <QESButton
                        qesTestId={`competitive-insights__network-selection--view-button-${index}`}
                        qesType="secondary"
                        className={cx(
                          "d-inline-block",
                          "p-2", {
                          "active": viewType === selectedView
                        })}
                        key={index}
                        onClick={() => setSelectedView(viewType)}
                      >
                        <QesIcon iconName={getViewIcon(viewType)} description={viewType} width="24" />
                      </QESButton>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid px-0">
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2">
                  <div className="tab-content">
                    {getVisibleTabs().map((tab, index) => (
                      <div
                        key={index}
                        className={`tab-pane fade${benchmarkingFiltersContext.selectedTab === tab ? " show" : ""}`} id={`${formatTabId(tab)}-tab`}
                      >
                        {getTabView(tab)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CompetitiveInsightsDefaultView;